"use client";

import { Activity, ExternalLink, EyeOff, Globe2, RadioTower, SlidersHorizontal, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/components/auth/auth-provider";
import { AnarchyGlobe } from "@/components/globe/anarchy-globe";
import { SiteShell } from "@/components/layout/site-shell";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useGlobeFrameStream } from "@/hooks/use-globe-frame-stream";
import {
  DEFAULT_GDELT_MOCK_FRAME_URL,
  EXPORT_SIGNAL_CATEGORY_STYLE,
  GEO_PRESSURE_TIME_WINDOWS,
  HOT_ZONE_CATEGORIES,
  buildGeoPressureIntelligenceHref,
  filterGlobeFrame,
  globeCameraStateToSearchParams,
  parseGeoPressureFilters,
  parseGlobeCameraUrlState,
  resolveGlobeStreamSource,
  type GlobeCameraUrlState,
  type GeoPressureFilterState,
} from "@/lib/gdelt-globe";
import { cn } from "@/lib/utils";
import { apiUrl } from "@/lib/client-environment";
import { GLOBE_VIEW_CONFIG } from "@/lib/data/globe-view-config";
import { SANDBOX_GLOBE_RENDER_SETTINGS } from "@/lib/data/globe-render-settings";
import {
  YOUTUBE_VIDEO_PIXEL_SIZE,
  YOUTUBE_VIDEO_MESH_SIZE,
  buildYouTubeEmbedUrl,
  normalizeYouTubeSearchParam,
  resolveYouTubeVideoId,
} from "@/lib/youtube-embed";
import type { GlobeFrame, GlobeMapMode, HotZone } from "@/types/gdelt";
import { isGdeltExportHotZone, type GdeltExportActor, type GdeltExportGeography, type GdeltExportHotZone } from "@/types/gdelt-export";

type GeoPressureIntelligenceClientProps = {
  initialFrame: GlobeFrame;
  initialFilters: GeoPressureFilterState;
  initialCameraState?: GlobeCameraUrlState | null;
  initialMapMode?: GlobeMapMode;
};

const DETAIL_ROLES = ["administrator", "super_admin", "tenant_admin", "developer", "editor"] as const;

export function GeoPressureIntelligenceClientSandbox({
  initialFrame,
  initialFilters,
  initialCameraState = null,
  initialMapMode = "outline",
}: GeoPressureIntelligenceClientProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const auth = useAuth();
  const [filters, setFilters] = useState(initialFilters);
  const [cameraState, setCameraState] = useState<GlobeCameraUrlState | null>(initialCameraState);
  const [cameraFov, setCameraFov] = useState(() => boundedNumber(searchParams.get("lens_fov"), GLOBE_VIEW_CONFIG.camera.fov, 0.35, 1.4));
  const [zoomInLimit, setZoomInLimit] = useState(() => boundedNumber(searchParams.get("zoom_min"), 0.45, 0.15, 2));
  const requestedYoutubeVideo = searchParams.get("youtube");
  const youtubeVideoId = resolveYouTubeVideoId(requestedYoutubeVideo);
  const youtubeSearchParam = normalizeYouTubeSearchParam(requestedYoutubeVideo);

  const source = useMemo(
    () => resolveGlobeStreamSource(apiUrl(`/api/gdelt/globe-frame?windowMinutes=${filters.tWindowMinutes}`)),
    [filters.tWindowMinutes],
  );
  const mapMode: GlobeMapMode = searchParams.get("mode") === "terrain" ? "terrain" : initialMapMode;
  const { frame, status, error } = useGlobeFrameStream(source, initialFrame);

  useEffect(() => {
    const params = Object.fromEntries(searchParams.entries());
    setFilters(parseGeoPressureFilters(params));
    setCameraState(parseGlobeCameraUrlState(params));
  }, [searchParams]);

  const filteredFrame = useMemo(() => {
    if (!frame) return null;
    return {
      ...filterGlobeFrame(frame, filters),
      window_minutes: filters.tWindowMinutes,
    };
  }, [filters, frame]);

  const selectedZone = useMemo(() => {
    if (!filteredFrame || !filters.zoneId) return null;
    return filteredFrame.zones.find((zone) => zone.zone_id === filters.zoneId) ?? null;
  }, [filteredFrame, filters.zoneId]);
  const selectedExportZone = selectedZone && isGdeltExportHotZone(selectedZone) ? selectedZone : null;

  const canViewDetails = auth.isAuthenticated && auth.hasAnyRole([...DETAIL_ROLES]);
  const canViewSensitiveMetrics = auth.canViewEmail();

  const updateFilters = (nextFilters: GeoPressureFilterState) => {
    setFilters(nextFilters);
    const href = buildGeoPressureIntelligenceHref(pathname, nextFilters, {
      source: source.url === DEFAULT_GDELT_MOCK_FRAME_URL ? null : source.url,
      mode: mapMode === "outline" ? null : mapMode,
      lens_fov: cameraFov.toFixed(2),
      zoom_min: zoomInLimit.toFixed(2),
      youtube: youtubeSearchParam,
      ...globeCameraStateToSearchParams(cameraState),
    });
    navigate(href, { replace: true });
  };

  const handleZoneSelect = (zone: HotZone) => {
    updateFilters({ ...filters, zoneId: zone.zone_id });
  };

  const handleCameraStateChange = (nextCameraState: GlobeCameraUrlState) => {
    setCameraState(nextCameraState);
    const href = buildGeoPressureIntelligenceHref(pathname, filters, {
      source: source.url === DEFAULT_GDELT_MOCK_FRAME_URL ? null : source.url,
      mode: mapMode === "outline" ? null : mapMode,
      lens_fov: cameraFov.toFixed(2),
      zoom_min: zoomInLimit.toFixed(2),
      youtube: youtubeSearchParam,
      ...globeCameraStateToSearchParams(nextCameraState),
    });
    window.history.replaceState(null, "", href);
  };

  const youtubeContainerRef = useRef<HTMLDivElement | null>(null);

  return (
    <SiteShell
      map={
        <AnarchyGlobe
          frame={filteredFrame ?? initialFrame}
          streamSource={source}
          mapMode={mapMode}
          selectedZoneId={filters.zoneId}
          onZoneSelect={handleZoneSelect}
          initialCameraState={cameraState}
          onCameraStateChange={handleCameraStateChange}
          showTelemetryOverlay={false}
          cameraFov={cameraFov}
          cameraLowerRadiusLimit={zoomInLimit}
          markerStyle="export"
          showProceduralLandShapes={false}
          renderSettings={SANDBOX_GLOBE_RENDER_SETTINGS}
          modelSourceUrl="/models/anarchy-globe.obj"
          infoCardModelSourceUrl="/models/event-info-card.obj"
          hoverDelayMs={80}
          eventOverlay={selectedExportZone ? (
            <EventInfoCard zone={selectedExportZone} onDismiss={() => updateFilters({ ...filters, zoneId: null })} />
          ) : null}
          eventPanelHtmlRef={youtubeContainerRef}
          eventPanelHtmlSize={YOUTUBE_VIDEO_MESH_SIZE}
          eventPanelHtmlContent={(
            <div
              ref={youtubeContainerRef}
              data-testid="youtube-html-mesh-content"
              style={{
                // Keep the source box measurable by HtmlMesh's fit wrapper so
                // 1024x576 maps uniformly onto the full 4x2.25 Babylon plane.
                position: "relative",
                width: `${YOUTUBE_VIDEO_PIXEL_SIZE.width}px`,
                height: `${YOUTUBE_VIDEO_PIXEL_SIZE.height}px`,
                backgroundColor: "#000",
                pointerEvents: "auto",
                visibility: "hidden",
              }}
            >
              <iframe
                width="100%"
                height="100%"
                src={buildYouTubeEmbedUrl(youtubeVideoId)}
                title="YouTube event video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                style={{ border: "none", display: "block" }}
              />
            </div>
          )}
          className="h-full min-h-[72vh] rounded-none border-0 lg:min-h-0"
        />
      }
      sidebar={
        <aside className="space-y-4">
          <section className="rounded-md border border-white/10 bg-card p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              <Globe2 className="h-3.5 w-3.5" aria-hidden />
              Global Pressure
            </div>
            <h1 className="mt-2 text-xl font-semibold">Export signal field</h1>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <Metric label="Zones" value={filteredFrame?.zones.length ?? 0} />
              <Metric label="Window" value={formatWindow(filters.tWindowMinutes)} />
              <Metric label="Intensity" value={filteredFrame?.global_intensity ?? initialFrame.global_intensity} />
            </div>
            <div className="mt-3 flex items-center gap-2 rounded-sm border border-cyan-300/15 bg-cyan-300/5 px-3 py-2 text-xs text-cyan-100">
              <RadioTower className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="truncate">{error ? error : status === "open" ? "Local GDELT Export overlay" : "Indexing Export dataset"}</span>
            </div>
          </section>

          <section className="rounded-md border border-white/10 bg-card p-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <SlidersHorizontal className="h-4 w-4" aria-hidden />
              Filters
            </div>

            <div className="mt-4 space-y-3">
              <ControlLabel label="Event category" value={filters.category === "all" ? "All" : EXPORT_SIGNAL_CATEGORY_STYLE[filters.category].label} />
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={filters.category === "all" ? "secondary" : "outline"}
                  onClick={() => updateFilters({ ...filters, category: "all", zoneId: null })}
                >
                  All
                </Button>
                {HOT_ZONE_CATEGORIES.map((category) => (
                  <Button
                    key={category}
                    type="button"
                    size="sm"
                    variant={filters.category === category ? "secondary" : "outline"}
                    className="justify-start"
                    onClick={() => updateFilters({ ...filters, category, zoneId: null })}
                  >
                    <span
                      className="h-2 w-2 rounded-full shadow-[0_0_14px_currentColor]"
                      style={{
                        color: EXPORT_SIGNAL_CATEGORY_STYLE[category].glow,
                        backgroundColor: EXPORT_SIGNAL_CATEGORY_STYLE[category].glow,
                      }}
                    />
                    <span className="truncate">{EXPORT_SIGNAL_CATEGORY_STYLE[category].label}</span>
                  </Button>
                ))}
              </div>
            </div>

            <div className="mt-5 space-y-3">
              <ControlLabel label="Time window" value={formatWindow(filters.tWindowMinutes)} />
              <div className="grid grid-cols-4 gap-2">
                {GEO_PRESSURE_TIME_WINDOWS.map((window) => (
                  <Button
                    key={window.minutes}
                    type="button"
                    size="sm"
                    variant={filters.tWindowMinutes === window.minutes ? "secondary" : "outline"}
                    onClick={() => updateFilters({ ...filters, tWindowMinutes: window.minutes })}
                  >
                    {window.label}
                  </Button>
                ))}
              </div>
              <Button type="button" size="sm" variant="ghost" disabled className="w-full justify-start">
                Longer windows pending stream behavior
              </Button>
            </div>

            <div className="mt-5 space-y-4 border-t border-white/10 pt-4">
              <ControlLabel label="Lens width" value={`${Math.round(cameraFov * 180 / Math.PI)}°`} />
              <Slider min={0.35} max={1.4} step={0.05} value={cameraFov} aria-label="Camera lens width" onValueChange={setCameraFov} />
              <ControlLabel label="Zoom-in limit" value={zoomInLimit.toFixed(2)} />
              <Slider min={0.15} max={2} step={0.05} value={zoomInLimit} aria-label="Camera zoom-in limit" onValueChange={setZoomInLimit} />
              <p className="text-xs leading-5 text-muted-foreground">A wider lens reveals more of the surrounding signal field. The radius limit controls how close the observer may orbit the current target while remaining inside the sphere.</p>
            </div>

            <div className="mt-5 space-y-3">
              <ControlLabel label="Intensity threshold" value={`${filters.minIntensity}+`} />
              <Slider
                min={0}
                max={100}
                step={5}
                value={filters.minIntensity}
                aria-label="Intensity threshold"
                onValueChange={(value) => updateFilters({ ...filters, minIntensity: value, zoneId: null })}
              />
            </div>
          </section>

          <section
            className={cn(
              "rounded-md border p-4 transition",
              selectedZone ? "border-cyan-300/30 bg-cyan-300/[0.06]" : "border-white/10 bg-card",
            )}
          >
            {selectedZone ? (
              <ZoneInfoCard zone={selectedZone} canViewDetails={canViewDetails} canViewSensitiveMetrics={canViewSensitiveMetrics} />
            ) : (
              <div className="py-4 text-sm text-muted-foreground">
                Left-click a hot zone to select it, fly the camera to its centroid, and open intelligence cards here.
              </div>
            )}
          </section>
        </aside>
      }
    />
  );
}

function EventInfoCard({ zone, onDismiss }: { zone: GdeltExportHotZone; onDismiss: () => void }) {
  const event = zone.sourceEvent;
  const style = EXPORT_SIGNAL_CATEGORY_STYLE[zone.category];
  return (
    <article className="event-card-enter max-h-[calc(100dvh-7rem)] overflow-y-auto border border-red-400/35 bg-[#05090d]/95 shadow-[0_0_42px_rgba(255,20,52,0.2)] backdrop-blur-xl">
      <header className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-white/10 bg-[#071017]/95 px-4 py-3 backdrop-blur">
        <div className="min-w-0">
          <p className="text-[0.68rem] font-semibold uppercase text-red-300">GDELT event signal</p>
          <div className="mt-1 flex items-center gap-2">
            <span className="h-2.5 w-2.5 shrink-0 rounded-full shadow-[0_0_16px_currentColor]" style={{ color: style.glow, backgroundColor: style.glow }} />
            <h2 className="truncate text-xl font-semibold">{zone.significantWord}</h2>
          </div>
          <p className="mt-1 truncate text-sm text-muted-foreground">{event.location.name || "Unknown location"}</p>
        </div>
        <button type="button" onClick={onDismiss} className="grid h-8 w-8 shrink-0 place-items-center rounded-sm border border-white/10 text-muted-foreground hover:bg-white/10 hover:text-foreground" aria-label="Dismiss event card">
          <X className="h-4 w-4" aria-hidden />
        </button>
      </header>

      <div className="space-y-4 p-4">
        <div className="grid grid-cols-3 gap-2 text-center">
          <EventMetric label="Pressure" value={zone.intensity} />
          <EventMetric label="Goldstein" value={event.goldsteinScale.toFixed(1)} />
          <EventMetric label="Tone" value={event.averageTone.toFixed(1)} />
        </div>

        <section>
          <p className="text-xs uppercase text-muted-foreground">Primary actors</p>
          <p className="mt-1 text-base font-medium">{actorLabel(event.actor1)} <span className="text-red-300">/</span> {actorLabel(event.actor2)}</p>
          <p className="mt-2 text-sm text-muted-foreground">CAMEO {event.eventCode} · root {event.eventRootCode} · quad {event.quadClass}</p>
        </section>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 border-y border-white/10 py-3 text-sm">
          <EventDatum label="Observed" value={formatEventDate(event.eventDate)} />
          <EventDatum label="Added" value={formatEventDate(event.addedAt)} />
          <EventDatum label="Mentions" value={String(event.mentionCount)} />
          <EventDatum label="Sources" value={String(event.sourceCount)} />
          <EventDatum label="Articles" value={String(event.articleCount)} />
          <EventDatum label="Cell events" value={String(zone.event_count)} />
        </div>

        <a href={event.sourceUrl} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-3 border border-white/10 px-3 py-2 text-sm text-cyan-200 hover:bg-white/5">
          <span className="truncate">Open source article</span><ExternalLink className="h-4 w-4 shrink-0" aria-hidden />
        </a>

        <details className="group border-t border-white/10 pt-3 text-sm">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Complete event record</summary>
          <div className="mt-3 space-y-4 text-xs">
            <DetailSection title="Identity" rows={[
              ["Global event ID", event.globalEventId], ["Event code", event.eventCode], ["Base code", event.eventBaseCode],
              ["Root code", event.eventRootCode], ["Root event", event.isRootEvent ? "Yes" : "No"], ["Quad class", String(event.quadClass)],
            ]} />
            <ActorDetails title="Actor 1" actor={event.actor1} />
            <ActorDetails title="Actor 2" actor={event.actor2} />
            <GeographyDetails title="Action geography" geography={event.location} />
            <GeographyDetails title="Actor 1 geography" geography={event.actor1Geography} />
            <GeographyDetails title="Actor 2 geography" geography={event.actor2Geography} />
          </div>
        </details>
      </div>
    </article>
  );
}

function EventMetric({ label, value }: { label: string; value: string | number }) {
  return <div className="border border-white/10 bg-white/[0.035] px-2 py-2"><p className="text-lg font-semibold">{value}</p><p className="text-[0.62rem] uppercase text-muted-foreground">{label}</p></div>;
}

function EventDatum({ label, value }: { label: string; value: string }) {
  return <div className="min-w-0"><p className="text-xs text-muted-foreground">{label}</p><p className="truncate font-medium">{value || "—"}</p></div>;
}

function ActorDetails({ title, actor }: { title: string; actor: GdeltExportActor }) {
  return <DetailSection title={title} rows={Object.entries(actor).map(([key, value]) => [humanize(key), value || "—"])} />;
}

function GeographyDetails({ title, geography }: { title: string; geography: GdeltExportGeography }) {
  return <DetailSection title={title} rows={Object.entries(geography).map(([key, value]) => [humanize(key), value === null || value === "" ? "—" : String(value)])} />;
}

function DetailSection({ title, rows }: { title: string; rows: string[][] }) {
  return <section><h3 className="mb-1 font-semibold uppercase text-foreground">{title}</h3><dl className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)] gap-x-3 gap-y-1">{rows.map(([label, value]) => <div key={label} className="contents"><dt className="text-muted-foreground">{label}</dt><dd className="break-words text-right">{value || "—"}</dd></div>)}</dl></section>;
}

function actorLabel(actor: GdeltExportActor) {
  return actor.name || actor.code || "Unspecified actor";
}

function formatEventDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function humanize(value: string) {
  return value.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (character) => character.toUpperCase());
}

function ZoneInfoCard({
  zone,
  canViewDetails,
  canViewSensitiveMetrics,
}: {
  zone: HotZone;
  canViewDetails: boolean;
  canViewSensitiveMetrics: boolean;
}) {
  const style = EXPORT_SIGNAL_CATEGORY_STYLE[zone.category];

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
            <Activity className="h-3.5 w-3.5" aria-hidden />
            Selected zone
          </div>
          <h2 className="mt-1 break-all text-lg font-semibold">{zone.zone_id}</h2>
        </div>
        <span className="rounded-sm px-2 py-1 text-xs font-medium text-black" style={{ backgroundColor: style.glow }}>
          {zone.trend}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <Metric label="Intensity" value={zone.intensity} />
        <Metric label="Events" value={zone.event_count} />
        <Metric label="Mentions" value={canViewSensitiveMetrics ? zone.mention_count : "Hidden"} />
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {zone.top_themes.map((theme) => (
          <span key={theme} className="rounded-sm bg-white/10 px-2 py-1 text-xs text-muted-foreground">
            {theme}
          </span>
        ))}
      </div>

      {canViewDetails ? (
        <div className="mt-4 grid gap-2 text-sm">
          <DetailRow label="Category" value={style.label} />
          <DetailRow label="Geohash" value={zone.geohash} />
          <DetailRow label="Centroid" value={`${zone.centroid.lat.toFixed(3)}, ${zone.centroid.lon.toFixed(3)}`} />
          <DetailRow label="Updated" value={new Date(zone.updated_at).toLocaleString()} />
        </div>
      ) : (
        <div className="mt-4 flex gap-2 rounded-sm border border-white/10 bg-black/20 px-3 py-2 text-xs text-muted-foreground">
          <EyeOff className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          Sign in with an authorized role to view geohash, exact centroid, and source-level metrics.
        </div>
      )}
    </div>
  );
}

function ControlLabel({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-sm border border-white/10 bg-white/[0.04] px-2 py-2">
      <p className="truncate text-base font-semibold">{value}</p>
      <p className="text-[0.68rem] uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-sm border border-white/10 bg-white/[0.03] px-3 py-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="break-all text-right font-medium">{value}</span>
    </div>
  );
}

function formatWindow(minutes: number) {
  if (minutes < 60) return `${minutes}m`;
  if (minutes % 60 === 0 && minutes < 1440) return `${minutes / 60}h`;
  if (minutes === 1440) return "24h";
  return `${minutes}m`;
}

function boundedNumber(value: string | null, fallback: number, minimum: number, maximum: number) {
  if (!value) return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.min(Math.max(parsed, minimum), maximum) : fallback;
}
